import React, { useEffect, useRef, useState } from "react";
import { Send, Bot, User as UserIcon } from "lucide-react";
import { api } from "../lib/api";
import MarkdownText from "./MarkdownText";

export default function LessonChat({ lessonId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/learn/chat/history/${lessonId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch((e) => console.error("Chat history load failed", e));
  }, [lessonId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text, _local: true }]);
    setBusy(true);
    try {
      const r = await api.post("/learn/chat", { lesson_id: lessonId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: r.data.assistant_message, _local: true }]);
    } catch (err) {
      console.error("Chat send failed", err);
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — the assistant is unavailable right now. Try again in a moment.", _error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col h-[500px] bg-[#060A24] border border-white/10" data-testid="lesson-chat">
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 no-scrollbar">
        {messages.length === 0 && (
          <div className="text-white/45 text-sm">
            <Bot className="mb-3 text-fc-red" size={22} />
            Ask anything about this lesson. Try: <em>"What's the first step?"</em> or <em>"Give me the KPI to track in week 1."</em>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex items-start gap-3 ${m.role === "user" ? "" : ""}`} data-testid={`chat-msg-${m.role}`}>
            <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center ${
              m.role === "user" ? "bg-white/10" : "bg-fc-red/15 text-fc-red"
            }`}>
              {m.role === "user" ? <UserIcon size={14} /> : <Bot size={14} />}
            </div>
            <div className={`flex-1 min-w-0 text-sm leading-relaxed ${
              m.role === "user" ? "text-white whitespace-pre-wrap" : "text-white/85"
            }`}>
              {m.role === "user" ? m.content : <MarkdownText variant="dark">{m.content}</MarkdownText>}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 bg-fc-red/15 text-fc-red flex items-center justify-center"><Bot size={14} /></div>
            <div className="text-white/50 text-sm italic">Freddy is thinking…</div>
          </div>
        )}
      </div>

      <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask anything about this lesson…"
          className="flex-1 bg-fc-navy border border-white/10 focus:border-fc-red text-white px-3 py-2 outline-none text-sm"
          data-testid="chat-input"
        />
        <button type="submit" disabled={busy || !input.trim()} className="btn-fc-red text-xs py-2 px-4" data-testid="chat-send-btn">
          <Send size={14} />
        </button>
      </form>
    </div>
  );
}
